import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { UsageService } from '../shared/services/usage.service';

/**
 * Inline usage banner shown above the chat composer (prototype "approaching /
 * reached" warnings). Amber while the caller nears a token limit, red once a
 * limit is hit — at which point the backend already blocks the turn with a
 * `quota_exceeded` frame, so this banner is the ambient, persistent companion
 * to that one-off message.
 *
 * No billing call-to-action: this deployment has no self-service plan/credits,
 * so the banner is purely informational (when usage resets) rather than an
 * "Upgrade plan" prompt.
 */
@Component({
  selector: 'app-usage-warning',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div class="usage-warn" [class.usage-warn--reached]="reached()" role="status">
        <span class="usage-warn__icon">
          <i class="pi pi-exclamation-triangle"></i>
        </span>
        <div class="usage-warn__text">
          <span class="usage-warn__title">{{ title() }}</span>
          <span class="usage-warn__body">{{ body() }}</span>
        </div>
        @if (!reached()) {
          <button class="usage-warn__dismiss" type="button"
            title="Dismiss" aria-label="Dismiss" (click)="dismiss()">
            <i class="pi pi-times"></i>
          </button>
        }
      </div>
    }
  `,
  styles: [`
    /* Colours + sizing mirror the prototype banner exactly (intentionally
     * theme-independent: a light cream/red card that pops on dark and light). */
    .usage-warn {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      max-width: 720px;
      margin: 0 auto 16px;
      padding: 12px 14px;
      border-radius: 13px;
      box-sizing: border-box;
      text-align: left;
      animation: usageWarnIn 0.2s ease;
      border: 1px solid #f3d9a6;
      background: linear-gradient(180deg, #fef6e7, #fffdf8);
    }
    .usage-warn--reached {
      border-color: #f3c0bf;
      background: linear-gradient(180deg, #fdeceb, #fff7f6);
    }
    .usage-warn__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      flex-shrink: 0;
      border-radius: 10px;
      background: #fbeccb;
      color: #b5701a;
    }
    .usage-warn--reached .usage-warn__icon {
      background: #fbdad7;
      color: #d8392f;
    }
    .usage-warn__icon .pi { font-size: 1.05rem; }
    .usage-warn__text {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 1px;
      line-height: 1.4;
    }
    .usage-warn__title {
      font-size: 13.5px;
      font-weight: 700;
      color: #3a2a12;
    }
    .usage-warn--reached .usage-warn__title { color: #4a1714; }
    .usage-warn__body {
      font-size: 12.5px;
      color: #6c5a3e;
    }
    .usage-warn--reached .usage-warn__body { color: #7a4f48; }
    .usage-warn__dismiss {
      flex-shrink: 0;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: #9b8a6a;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.12s, color 0.12s;
    }
    .usage-warn__dismiss:hover {
      background: rgba(0, 0, 0, 0.06);
      color: #6c5a3e;
    }
    .usage-warn__dismiss .pi { font-size: 0.78rem; }
    @keyframes usageWarnIn {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
  `],
})
export class UsageWarningComponent {
  private readonly svc = inject(UsageService);

  // Hub chat consumes only the END_USER ledger and is enforced per-ledger, so the
  // pause banner must reflect that ledger — NOT the combined level (where Studio/
  // BUILDER usage could falsely show Hub as paused).
  readonly level = this.svc.endUserLevel;
  readonly reached = computed(() => this.level() === 'reached');

  readonly visible = computed(() => {
    const l = this.level();
    if (l === 'reached') return true;                       // never dismissable
    if (l === 'approaching') return !this.svc.warningDismissed();
    return false;
  });

  /** 'daily' | 'monthly' — the END_USER window the banner reports on. */
  private readonly scope = this.svc.endUserWorstScope;
  private readonly scopeLabel = computed(() => this.scope());  // already 'daily'/'monthly'

  title = computed(() =>
    this.reached()
      ? `You've reached your ${this.scopeLabel()} limit`
      : `You're approaching your ${this.scopeLabel()} limit`);

  body = computed(() => {
    // Read the END_USER ledger window the banner reports on. The banner only
    // becomes visible when that ledger has an approaching/reached level, which
    // requires the ledger to be present — so a null here means nothing to show.
    const lg = this.svc.endUserLedger();
    if (!lg) return '';
    const win = this.scope() === 'daily' ? lg.daily : lg.monthly;
    const pct = Math.min(100, Math.round(win.pct_used ?? 0));
    const resets = this.resetLabel(win.resets_at);
    if (this.reached()) {
      return resets
        ? `New messages are paused until your ${this.scopeLabel()} limit resets ${resets}.`
        : `New messages are paused until your ${this.scopeLabel()} limit resets.`;
    }
    return resets
      ? `You've used ${pct}% of your ${this.scopeLabel()} tokens. Resets ${resets}.`
      : `You've used ${pct}% of your ${this.scopeLabel()} tokens.`;
  });

  dismiss(): void {
    this.svc.dismissWarning();
  }

  /** Friendly reset label: a time for the daily window, a date for the monthly. */
  private resetLabel(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return this.scope() === 'daily'
      ? `at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
      : `on ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }
}
